import { Modal } from "@modernatx-internal/component-library";
import { Router } from "next/router";
import React from "react";

import type { StreamingVideoProps } from "./StreamingVideo";

export interface VideoModalProps {
  video: React.ReactNode;
  videoModalId: string;
}

const getHashValue = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return decodeURIComponent(window.location.hash.replace(/^#/, ""));
};

const normalizeHashValue = (value: string) => value.toLowerCase();

export const VideoModal: React.FC<VideoModalProps> = ({ video, videoModalId }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [, setIsHashActivated] = React.useState(false);
  const videoElement = React.isValidElement<StreamingVideoProps>(video) ? video : null;
  const videoProps = videoElement?.props;

  React.useEffect(() => {
    if (!videoModalId) {
      return;
    }

    const openFromHash = () => {
      if (normalizeHashValue(getHashValue()) === normalizeHashValue(videoModalId)) {
        setIsOpen(true);
        setIsHashActivated(true);
      }
    };

    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    // Same-page navigations triggered via the app's client-side router (e.g.
    // clicking a "Watch Video" link) update the URL via the History API,
    // which does not fire a native "hashchange" event. Listening for
    // Next.js's route change events covers that case too.
    Router.events.on("routeChangeComplete", openFromHash);
    Router.events.on("hashChangeComplete", openFromHash);

    return () => {
      window.removeEventListener("hashchange", openFromHash);
      Router.events.off("routeChangeComplete", openFromHash);
      Router.events.off("hashChangeComplete", openFromHash);
    };
  }, [videoModalId]);

  const handleDismiss = () => {
    if (
      typeof window !== "undefined" &&
      normalizeHashValue(getHashValue()) === normalizeHashValue(videoModalId)
    ) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }

    setIsOpen(false);
    setIsHashActivated(false);
  };

  const accessibleLabel =
    typeof videoProps?.title === "string" && videoProps.title.trim() ? videoProps.title : "Video";

  return (
    <Modal aria-label={accessibleLabel} isOpen={isOpen} size="lg" onDismiss={handleDismiss}>
      {videoElement}
    </Modal>
  );
};
